import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import ApexCharts from "react-apexcharts";
import { Card } from "./Card";
import { useTheme } from "@mui/material/styles";
import {
    useChartGenerator,
    priceDenomination,
    formatDate,
} from "../helpers/ChartHelpers";

// Regular Bar Chart Component
export function BarChart({
    data,
    series,
    xLabel,
    yLabel,
    title,
    xKey,
    stacked,
    yOverlap,
    isCategoryX,
    yFormatter,
}) {
    const chartRef = useRef(null);
    const generateChartData = useChartGenerator();
    const chartData = generateChartData(
        data,
        series,
        xKey,
        {
            x: xLabel,
            y: yLabel,
            title: title,
            yFormatter,
        },
        "bar",
        stacked,
        yOverlap,
        isCategoryX
    );

    // Modify x-axis options for better readability
    chartData.options.xaxis = {
        ...chartData.options.xaxis,
        type: "category",
        labels: {
            rotate: -45,
            rotateAlways: true,
            // maxHeight: 95,
        },
    };

    // Add this useEffect to ensure chart is properly rendered for PDF export
    useEffect(() => {
        if (chartRef.current?.chart) {
            // Force redraw after component mounts to ensure proper rendering for export
            const chart = chartRef.current.chart;

            // Use ResizeObserver to ensure chart renders correctly when container size changes
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            // Get the chart's container element
            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, series]);

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                height={350}
                type="bar"
            />
        </Card>
    );
}

BarChart.propTypes = {
    data: PropTypes.array.isRequired,
    series: PropTypes.array.isRequired,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    title: PropTypes.string,
    xKey: PropTypes.string,
    stacked: PropTypes.bool,
    yOverlap: PropTypes.array,
    isCategoryX: PropTypes.bool,
    yFormatter: PropTypes.func,
};

// New Stacked Bar Chart Component
export function StackedBarChart({
    data,
    series,
    xLabel,
    yLabel,
    title,
    xKey,
    isCategoryX,
    yFormatter,
}) {
    const chartRef = useRef(null);
    const generateChartData = useChartGenerator();

    // Move the empty data check after hooks
    const isEmpty = !data || data?.length === 0;

    const chartData = !isEmpty
        ? generateChartData(
              data,
              series,
              xKey,
              {
                  x: xLabel,
                  y: yLabel,
                  title: title,
                  yFormatter,
              },
              "bar",
              true, // stacked is set to true for stacked bar chart
              [],
              isCategoryX
          )
        : { options: {}, series: [] };

    // Enhanced styling for stacked bars if we have data
    if (!isEmpty) {
        chartData.options.plotOptions = {
            ...chartData.options.plotOptions,
            bar: {
                ...chartData.options.plotOptions.bar,
                columnWidth: "70%",
                endingShape: "rounded",
                dataLabels: {
                    position: "center",
                    total: {
                        enabled: true,
                        style: {
                            fontSize: "13px",
                            fontWeight: 900,
                        },
                        formatter: function (val) {
                            return priceDenomination(val);
                        },
                    },
                },
            },
        };

        // Add fill opacity gradient for better visual distinction
        chartData.options.fill = {
            opacity: 1,
            type: "gradient",
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.25,
                gradientToColors: undefined,
                inverseColors: true,
                opacityFrom: 1,
                opacityTo: 0.85,
                stops: [0, 100],
            },
        };

        // Ensure x-axis is treated as categories
        chartData.options.xaxis = {
            ...chartData.options.xaxis,
            type: "category",
            labels: {
                rotate: -45,
                rotateAlways: true,
            },
        };
    }

    // Add this useEffect to ensure chart is properly rendered for PDF export
    useEffect(() => {
        if (!isEmpty && chartRef?.current?.chart) {
            // Force redraw after component mounts to ensure proper rendering for export
            const chart = chartRef.current.chart;

            // Use ResizeObserver to ensure chart renders correctly when container size changes
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            // Get the chart's container element
            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, series, isEmpty]);

    // Return early after all hooks have been called
    if (isEmpty) {
        return <Card className="export-section">No data available</Card>;
    }

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                height={350}
                type="bar"
            />
        </Card>
    );
}

StackedBarChart.propTypes = {
    data: PropTypes.array,
    series: PropTypes.array.isRequired,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    title: PropTypes.string,
    xKey: PropTypes.string.isRequired,
    isCategoryX: PropTypes.bool,
    yFormatter: PropTypes.func,
};

// Line Chart Component
export function LineChart({
    data,
    series,
    xLabel,
    yLabel,
    title,
    xKey,
    xFormatter,
    yFormatter,
}) {
    const chartRef = useRef(null);
    const generateChartData = useChartGenerator();
    const chartData = generateChartData(
        data,
        series,
        xKey,
        {
            x: xLabel,
            y: yLabel,
            title: title,
            xFormatter,
            yFormatter,
        },
        "line"
    );

    // Add this useEffect to ensure chart is properly rendered for PDF export
    useEffect(() => {
        if (chartRef.current?.chart) {
            // Force redraw after component mounts to ensure proper rendering for export
            const chart = chartRef.current.chart;

            // Use ResizeObserver to ensure chart renders correctly when container size changes
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            // Get the chart's container element
            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, series]);

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                height={350}
                type="line"
            />
        </Card>
    );
}

LineChart.propTypes = {
    data: PropTypes.array.isRequired,
    series: PropTypes.array.isRequired,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    title: PropTypes.string,
    xKey: PropTypes.string,
    xFormatter: PropTypes.func,
    yFormatter: PropTypes.func,
};

// Horizontal Bar Chart Component
export function HorizontalBarChart({
    data,
    series,
    xLabel,
    yLabel,
    title,
    xKey,
    isCategoryX,
}) {
    const chartRef = useRef(null);
    const generateChartData = useChartGenerator();

    // Move the empty data check after hooks
    const isEmpty = !data || data?.length === 0;

    const chartData = !isEmpty
        ? generateChartData(
              data,
              series,
              xKey,
              {
                  x: xLabel,
                  y: yLabel,
                  title: title,
              },
              "bar",
              false, // stacked should be false for horizontal bar charts
              [],
              isCategoryX
          )
        : { options: {}, series: [] };

    // Modify options for horizontal bar chart if we have data
    if (!isEmpty) {
        chartData.options.plotOptions = {
            bar: {
                horizontal: true,
                borderRadius: 4,
            },
        };

        // Ensure x-axis is treated as categories
        chartData.options.xaxis = {
            ...chartData.options.xaxis,
            type: "category",
            categories: data.map((item) => item[xKey]), // Use category names as categories
            labels: {
                formatter: function (value) {
                    return isCategoryX ? value : formatDate(value);
                },
            },
        };

        // Swap x and y axis titles for horizontal bar chart
        [chartData.options.xaxis.title, chartData.options.yaxis.title] = [
            chartData.options.yaxis.title,
            chartData.options.xaxis.title,
        ];
    }

    // Add this useEffect to ensure chart is properly rendered for PDF export
    useEffect(() => {
        if (!isEmpty && chartRef.current?.chart) {
            // Force redraw after component mounts to ensure proper rendering for export
            const chart = chartRef.current.chart;

            // Use ResizeObserver to ensure chart renders correctly when container size changes
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            // Get the chart's container element
            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, series, isEmpty]);

    // Return early after all hooks have been called
    if (isEmpty) {
        return <Card className="export-section">No data available</Card>;
    }

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                height={350}
                type="bar"
            />
        </Card>
    );
}

HorizontalBarChart.propTypes = {
    data: PropTypes.array,
    series: PropTypes.array.isRequired,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    title: PropTypes.string,
    xKey: PropTypes.string.isRequired,
    isCategoryX: PropTypes.bool,
};

// Pie Chart Component
export function PieChart({ data, series, title, xKey, noSum }) {
    const chartRef = useRef(null);
    const theme = useTheme();

    // Calculate the sum for each series
    const seriesData = series.map((item) => ({
        name: item.name,
        value: data.reduce((sum, dataItem) => {
            // If xKey is provided, use it to filter data
            if (xKey) {
                if (item.key) {
                    if (noSum) {
                        return dataItem[xKey] == item.key
                            ? sum + dataItem[item.ref]
                            : sum;
                    }
                    return dataItem[xKey] === item.key ? sum + 1 : sum;
                }
                return dataItem[xKey];
            }
            // Otherwise, sum the values directly
            return sum + (dataItem[item.key] || 0);
        }, 0),
    }));

    const chartData = {
        series: seriesData.map((item) => item.value),
        options: {
            chart: {
                type: "donut",
                animations: {
                    enabled: false, // Disable animations for PDF export
                },
            },
            labels: seriesData.map((item) => item.name),
            title: {
                text: title,
                align: "left",
            },
            legend: {
                position: "bottom",
                horizontalAlign: "center",
            },
            colors: [
                theme.palette.graph.cyan,
                theme.palette.graph.yellow,
                theme.palette.graph.magenta,
                theme.palette.graph.purple,
                theme.palette.graph.pink,
                theme.palette.graph.orange,
                theme.palette.graph.green,
            ],
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val.toFixed(0);
                    },
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        chart: {
                            width: 200,
                        },
                        legend: {
                            position: "bottom",
                        },
                    },
                },
            ],
            dataLabels: {
                enabled: true,
                formatter: function (val, opt) {
                    return (
                        opt.w.globals.labels[opt.seriesIndex] +
                        ": " +
                        val.toFixed(1) +
                        "%"
                    );
                },
            },
        },
    };

    // Add this useEffect to ensure chart is properly rendered for PDF export
    useEffect(() => {
        if (chartRef.current?.chart) {
            // Force redraw after component mounts to ensure proper rendering for export
            const chart = chartRef.current.chart;

            // Use ResizeObserver to ensure chart renders correctly when container size changes
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            // Get the chart's container element
            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, series]);

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                type="donut"
                height={350}
            />
        </Card>
    );
}

PieChart.propTypes = {
    data: PropTypes.array.isRequired,
    series: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    title: PropTypes.string,
    xKey: PropTypes.string,
    noSum: PropTypes.bool,
};

// Alternative Pie Chart Component
export function PieChart2({ data, title, labelKey, dataKey }) {
    const chartRef = useRef(null);
    const theme = useTheme();

    // Calculate the sum for each series
    const seriesData = data.map((item) => ({
        name: item[labelKey],
        value: item[dataKey],
    }));

    const chartData = {
        series: seriesData.map((item) => item.value),
        options: {
            chart: {
                type: "donut",
                animations: {
                    enabled: false, // Disable animations for PDF export
                },
            },
            labels: seriesData.map((item) => item.name),
            title: {
                text: title,
                align: "left",
            },
            legend: {
                position: "bottom",
                horizontalAlign: "center",
            },
            colors: [
                theme.palette.graph.cyan,
                theme.palette.graph.yellow,
                theme.palette.graph.magenta,
                theme.palette.graph.purple,
                theme.palette.graph.pink,
                theme.palette.graph.orange,
                theme.palette.graph.green,
            ],
            tooltip: {
                y: {
                    formatter: function (val) {
                        return priceDenomination(val);
                    },
                },
            },
            responsive: [
                {
                    breakpoint: 480,
                    options: {
                        chart: {
                            width: 200,
                        },
                        legend: {
                            position: "bottom",
                        },
                    },
                },
            ],
            dataLabels: {
                enabled: true,
                formatter: function (val, opt) {
                    return (
                        opt.w.globals.labels[opt.seriesIndex] +
                        ": " +
                        val.toFixed(1) +
                        "%"
                    );
                },
            },
        },
    };

    // Add this useEffect to ensure chart is properly rendered for PDF export
    useEffect(() => {
        if (chartRef.current?.chart) {
            // Force redraw after component mounts to ensure proper rendering for export
            const chart = chartRef.current.chart;

            // Use ResizeObserver to ensure chart renders correctly when container size changes
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            // Get the chart's container element
            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, title]);

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                type="donut"
                height={350}
            />
        </Card>
    );
}

PieChart2.propTypes = {
    data: PropTypes.array.isRequired,
    title: PropTypes.string,
    labelKey: PropTypes.string.isRequired,
    dataKey: PropTypes.string.isRequired,
};

// Add CSS for chart titles
const styles = `
.chart-title {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 16px;
    font-weight: 600;
}

@media print {
    .export-section {
        page-break-inside: avoid;
    }
    
    .chart-title {
        font-size: 14px;
    }
}
`;

// Create and inject a style element
if (typeof document !== "undefined") {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
}

export function AreaChart({
    data,
    series,
    xLabel,
    yLabel,
    title,
    xKey,
    stacked,
    yOverlap,
    isCategoryX,
    yFormatter,
}) {
    const chartRef = useRef(null);
    const generateChartData = useChartGenerator();

    const chartData = generateChartData(
        data,
        series,
        xKey,
        {
            x: xLabel,
            y: yLabel,
            title: title,
            yFormatter,
        },
        "area",
        stacked,
        yOverlap,
        isCategoryX
    );

    chartData.options.xaxis = {
        ...chartData.options.xaxis,
        type: "category",
        labels: {
            rotate: -45,
            rotateAlways: true,
        },
    };

    useEffect(() => {
        if (chartRef.current?.chart) {
            const chart = chartRef.current.chart;
            const resizeObserver = new ResizeObserver(() => {
                setTimeout(() => {
                    chart.render();
                }, 10);
            });

            const chartContainer = chartRef.current.el;
            if (chartContainer) {
                resizeObserver.observe(chartContainer);
            }

            return () => {
                if (chartContainer) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [data, series]);

    return (
        <Card className="export-section">
            <ApexCharts
                ref={chartRef}
                series={chartData.series}
                options={chartData.options}
                height={350}
                type="area"
            />
        </Card>
    );
}

AreaChart.propTypes = {
    data: PropTypes.array.isRequired,
    series: PropTypes.array.isRequired,
    xLabel: PropTypes.string,
    yLabel: PropTypes.string,
    title: PropTypes.string,
    xKey: PropTypes.string,
    stacked: PropTypes.bool,
    yOverlap: PropTypes.array,
    isCategoryX: PropTypes.bool,
    yFormatter: PropTypes.func,
};
